import React, { useState } from "react";
import { Wand2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";
import { PageHeader, ToggleRow } from "@/components/finishing/Controls";
import { CandidateRow, CompareVideos } from "@/components/finishing/CandidateRow";

const OPS = [
  ["stabilize", "Stabilization", "libvidstab 2-pass, for clips flagged shaky"],
  ["denoise", "Denoise", "hqdn3d temporal denoise, for clips flagged noisy"],
  ["sharpen", "Sharpen / deblur", "unsharp mask, for clips flagged blurry"],
  ["upscale", "AI upscale ×2", "OpenCV DNN super-resolution for sources under 720p"],
];

export default function Enhancement() {
  const { projectId } = useApp();
  const { data, setData, refresh } = usePoll(() => (projectId ? api.getEnhancement(projectId) : Promise.resolve(null)), 3000, [projectId]);
  const [compare, setCompare] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!projectId) return <NoProject title="Enhancement needs a project" />;
  if (!data) return <div className="p-8"><Loader2 className="animate-spin text-[#E6B04E]" /></div>;

  const s = data.settings;
  const set = async (patch) => { try { setData(await api.setEnhancement(projectId, patch)); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const run = async (ids) => {
    setBusy(true);
    try { const r = await api.runEnhancement(projectId, ids); toast.success(`${r.queued} clip(s) queued`); refresh(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const revert = async (id) => { await api.revertEnhancement(id); toast.success("Reverted to original"); setCompare(null); refresh(); };
  const cmp = data.candidates.find((c) => c.id === compare);

  return (
    <div className="p-8 fade-up space-y-6">
      <PageHeader icon={Wand2} title="Enhancement Pass"
        subtitle="Opt-in repair that only touches clips the quality engine flagged — originals are never modified">
        <button className="btn btn-gold" onClick={() => run(null)} disabled={busy || !data.candidates.length} data-testid="btn-enhance-all">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Enhance flagged clips
        </button>
      </PageHeader>

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
        <div className="studio-card p-5 space-y-4">
          <ToggleRow label="Use enhanced clips in render" desc="Swap in repaired versions when rendering" on={s.use_in_render}
            onClick={() => set({ use_in_render: !s.use_in_render })} testid="enh-use-in-render" />
          {OPS.map(([k, l, d]) => (
            <ToggleRow key={k} label={l} desc={d} on={s.ops_enabled[k]} testid={`enh-op-${k}`}
              onClick={() => set({ ops_enabled: { [k]: !s.ops_enabled[k] } })} />
          ))}
          <div>
            <label className="metric-label block mb-1">Upscale model</label>
            <select className="studio-input !py-1.5 text-sm" value={s.model} onChange={(e) => set({ model: e.target.value })} data-testid="enh-model-select">
              {["espcn", "fsrcnn"].map((m) => <option key={m} value={m} disabled={!data.upscaler.models.includes(m)}>{`${m.toUpperCase()} ×2${data.upscaler.models.includes(m) ? "" : " (missing)"}`}</option>)}
            </select>
            <p className="text-[#64748B] text-xs mt-1" data-testid="enh-upscaler-status">
              {data.upscaler.dnn ? "DNN super-resolution ready (CPU)" : "DNN unavailable — falls back to lanczos + sharpen"} · long clips use lanczos
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 content-start">
          {[["Clips", data.total_clips], ["Analyzed", data.analyzed_clips], ["Flagged", data.candidates.length], ["Enhanced", data.enhanced]].map(([l, v]) => (
            <div key={l} className="studio-card p-4"><div className="metric-label">{l}</div><div className="font-head text-2xl font-bold" data-testid={`enh-metric-${l.toLowerCase()}`}>{v}</div></div>
          ))}
        </div>
      </div>

      {cmp && <CompareVideos c={cmp} onClose={() => setCompare(null)} />}

      <div className="space-y-3">
        <h2 className="font-head text-lg font-bold">Flagged clips</h2>
        {data.candidates.length === 0 ? (
          <div className="studio-card p-6 text-sm text-[#64748B]" data-testid="enh-empty">
            {data.analyzed_clips ? "No clips need enhancement — everything passed quality analysis." : "Run quality analysis (AI Analysis page) first so clips can be flagged."}
          </div>
        ) : data.candidates.map((c) => (
          <CandidateRow key={c.id} c={c} onRun={() => run([c.id])} onRevert={() => revert(c.id)} onCompare={() => setCompare(c.id)} />
        ))}
      </div>
    </div>
  );
}
